import type { MouseEvent, ReactNode } from "react";
import { Video } from "lucide-react";
import { resolveChatAvatarUrl } from "@/lib/chatAvatar";

export function ClientPill({ name }: { name: string }) {
  const label = name.trim();
  if (!label) return null;
  return (
    <span className="chat-client-pill" title={label}>
      {label}
    </span>
  );
}

export function ConvTypePill({ type, isRoom }: { type: string; isRoom?: boolean }) {
  if (type !== "group") return null;
  const label = isRoom ? "Salle" : "Groupe";
  return (
    <span
      className={`chat-type-pill ${isRoom ? "chat-type-pill--room" : "chat-type-pill--group"}`}
      title={isRoom ? "Salle de visio" : "Groupe"}
    >
      {label}
    </span>
  );
}

export function AvatarBubble({
  label,
  url,
  presence,
  online,
  size = "md",
  onClick,
  title,
}: {
  label: string;
  url?: string | null;
  /** online | dnd | meeting | incall | offline */
  presence?: string | null;
  /** @deprecated Prefer `presence` */
  online?: boolean;
  size?: "sm" | "md" | "lg";
  onClick?: (e: MouseEvent) => void;
  title?: string;
}) {
  const dim = size === "lg" ? "size-12" : size === "sm" ? "size-9" : "size-11";
  const dot = size === "lg" ? "size-3.5" : size === "sm" ? "size-2.5" : "size-3";
  const className = `relative flex ${dim} shrink-0 items-center justify-center rounded-full`;
  const face = (
    <span className="relative flex size-full items-center justify-center overflow-hidden rounded-full bg-[var(--chat-accent-soft)] text-sm font-semibold text-[var(--chat-accent-strong)]">
      {url ? (
        <img src={resolveChatAvatarUrl(url) ?? url} alt="" className="size-full object-cover" />
      ) : (
        label.slice(0, 1).toUpperCase()
      )}
    </span>
  );
  const p = (presence || (online ? "online" : "offline")).toLowerCase();
  let status: ReactNode = null;
  if (p === "online") {
    status = (
      <span
        className={`chat-online-dot absolute -right-0.5 -top-0.5 ${dot} rounded-full border-2 border-[var(--chat-surface)] bg-[var(--chat-online)]`}
        title="En ligne"
        aria-label="En ligne"
      />
    );
  } else if (p === "dnd") {
    status = (
      <span
        className={`chat-online-dot chat-presence-dot--dnd absolute -right-0.5 -top-0.5 ${dot} rounded-full border-2 border-[var(--chat-surface)]`}
        title="Ne pas déranger"
        aria-label="Ne pas déranger"
      />
    );
  } else if (p === "meeting") {
    status = (
      <span
        className={`chat-online-dot chat-presence-dot--meeting absolute -right-0.5 -top-0.5 ${dot} rounded-full border-2 border-[var(--chat-surface)]`}
        title="En réunion"
        aria-label="En réunion"
      />
    );
  } else if (p === "incall") {
    const incallDot = size === "lg" ? "size-4" : "size-3.5";
    status = (
      <span
        className={`chat-online-dot chat-presence-dot--incall absolute -right-0.5 -top-0.5 flex ${incallDot} items-center justify-center rounded-full border-2 border-[var(--chat-surface)]`}
        title="En visio"
        aria-label="En visio"
      >
        <Video className={size === "lg" ? "size-2.5" : "size-2"} strokeWidth={2.75} />
      </span>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        className={`${className} cursor-pointer transition hover:brightness-105 active:scale-95`}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e);
        }}
        title={title ?? (url ? "Voir la photo" : undefined)}
        aria-label={title ?? (url ? `Voir la photo de ${label}` : label)}
      >
        {face}
        {status}
      </button>
    );
  }
  return (
    <span className={className}>
      {face}
      {status}
    </span>
  );
}

export function ChatRoundCheck({
  checked,
  label,
}: {
  checked: boolean;
  label?: string;
}) {
  return (
    <span
      className={`chat-round-check inline-flex size-5 shrink-0 items-center justify-center rounded-full border-2 transition ${
        checked
          ? "border-[var(--chat-accent)] bg-[var(--chat-accent)] text-[#0f172a]"
          : "border-[var(--chat-border)] bg-[var(--chat-surface)] text-transparent"
      }`}
      aria-hidden
    >
      <svg viewBox="0 0 16 16" className="size-3" fill="none" aria-hidden>
        <path
          d="M3.5 8.2 6.4 11l6.1-6.4"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  );
}
